import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { useReveal } from '@hooks';
import PhotoStack from '@components/photostack';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 220px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: var(--space-2);
      padding-left: var(--space-5);
      font-family: var(--font-sans);
      font-size: var(--fz-lg);
      line-height: var(--lh-body);

      /* line-height: inherit puts the glyph on the same line box as the
         label, instead of the hardcoded 12px that left it floating high. */
      /* No font-size override: a smaller glyph resolves the unitless
         line-height against its own size, producing a shorter line box than
         the label and sitting high. Section 2's bullets inherit, and align. */
      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--accent);
        line-height: inherit;
      }
    }
  }
`;
const About = () => {
  // Every image in content/photos, ordered by filename — prefix new ones with
  // a number to place them. The first is the default top card.
  const data = useStaticQuery(graphql`
    query {
      photos: allFile(
        filter: {
          relativeDirectory: { eq: "photos" }
          extension: { in: ["jpg", "jpeg", "png", "webp"] }
        }
        sort: { fields: name, order: ASC }
      ) {
        nodes {
          id
          name
          childImageSharp {
            gatsbyImageData(width: 600, quality: 90, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
    }
  `);

  const photos = data.photos.nodes
    .filter(node => node.childImageSharp)
    .map(node => ({
      id: node.id,
      image: getImage(node.childImageSharp),
      // "02-wakesurfing" -> "Wakesurfing"
      alt: node.name.replace(/^\d+[-_]?/, '').replace(/[-_]/g, ' ') || 'Photo of Rain Chong',
    }));

  const revealContainer = useReveal();

  const skills = ['Python', 'C/C++', 'PyTorch', 'Node.js', 'LangChain', 'NestJS'];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! My name is Rain, and my work focuses on understanding and constructing
              intelligence. What began as a curiosity at 16 about consciousness has grown into a
              formal investigation of intelligent systems, both natural and artificial.
            </p>
            <p>
              I graduated from <a href="https://www.cuhk.edu.hk/english/index.html">CUHK</a> in 2025
              with a <a href="https://admission.cuhk.edu.hk/programme/aistn/">major in AI</a> and
              minors in Physics and Economics. I have worked across applied AI, research, and
              venture, including roles in automation system, quantitative trading, and early-stage
              company building. These experiences shaped a systems-level view of how intelligence
              can be engineered and deployed.
            </p>
            <p>
              My <em className="emphasis">current research</em> examines the topological and
              functional structure of cognition, with the goal of modeling learning, memory, and
              awareness as transformations on evolving state spaces, in the pursuit of general
              intelligence. I also write <a href="#blogs">blogs</a> regularly on AI, consciousness,
              and the future of intelligence.
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <PhotoStack photos={photos} />
      </div>
    </StyledAboutSection>
  );
};

export default About;
